import { useLayoutEffect, useState, type RefObject } from "react"

export interface DropdownPosition {
  left: number
  width: number
  maxHeight: number
  placement: "above" | "below"
  top?: number
  bottom?: number
}

export function useDropdownPosition(triggerRef: RefObject<HTMLElement | null>, open: boolean): DropdownPosition | null {
  const [position, setPosition] = useState<DropdownPosition | null>(null)

  useLayoutEffect(() => {
    if (!open) return

    const updatePosition = () => {
      const trigger = triggerRef.current
      if (!trigger) return

      const rect = trigger.getBoundingClientRect()
      const viewportPadding = 8
      const gap = 8
      const availableBelow = window.innerHeight - rect.bottom - viewportPadding - gap
      const availableAbove = rect.top - viewportPadding - gap
      const placement = availableBelow < 220 && availableAbove > availableBelow ? "above" : "below"
      const availableHeight = placement === "above" ? availableAbove : availableBelow
      const width = Math.min(rect.width, window.innerWidth - viewportPadding * 2)

      setPosition({
        left: Math.min(Math.max(viewportPadding, rect.left), window.innerWidth - width - viewportPadding),
        width,
        maxHeight: Math.max(80, Math.min(320, availableHeight)),
        placement,
        ...(placement === "above"
          ? { bottom: window.innerHeight - rect.top + gap }
          : { top: rect.bottom + gap }),
      })
    }

    updatePosition()
    window.addEventListener("resize", updatePosition)
    window.addEventListener("scroll", updatePosition, true)
    return () => {
      window.removeEventListener("resize", updatePosition)
      window.removeEventListener("scroll", updatePosition, true)
    }
  }, [open, triggerRef])

  return position
}
